/**
 * T2 Business — how the two pivoted grids present the columns the server declares.
 *
 * Nothing here decides what the answer is. Every figure compared, titled or drawn is one the
 * response already carries (§4.3); this arranges them — titles the year columns with the years the
 * response returned, drops the months that have not happened yet, and rides each quantity with the
 * direction it moved.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import type { ColumnDef } from '@tanstack/react-table';

import { defaultFormat, integer, type Formatter } from '@/components/report/groupedColumns';
import type { ReportColumn } from '@/lib/api';
import { cn } from '@/lib/utils';

type Row = Record<string, unknown>;

/**
 * A year column's value as a label, or null when the response carries none. Declared `string` but
 * computed as `:year - 2`, so it can arrive either way — read both rather than betting on one.
 */
function yearLabel(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The arrow a figure rides. Green up and red down is a **direction of travel**, not a verdict:
 * nothing here decides that buying more from a supplier is good, which is why it takes the
 * `delta-*` tokens rather than the `status-*` ones (`index.css`). Colour is never the only channel
 * (§4.9) — the arrow points, and the words behind it name both sides of the comparison.
 */
function TrendArrow({
  current,
  prior,
  currentLabel,
  priorLabel,
}: {
  current: number;
  prior: number;
  currentLabel: string | null;
  priorLabel: string | null;
}) {
  if (current === prior) {
    return null;
  }
  const up = current > prior;
  const change = prior === 0 ? null : ((current - prior) / prior) * 100;
  const subject = currentLabel ?? 'The latest period';
  const against = priorLabel ?? 'the one before';
  const words =
    change === null
      ? `${subject} up on ${against}, which had none`
      : `${subject} ${up ? 'up' : 'down'} ${integer.format(Math.abs(Math.round(change)))}% on ${against}`;

  return (
    <span title={words}>
      <FontAwesomeIcon
        icon={up ? faArrowUp : faArrowDown}
        className={cn('size-3 shrink-0', up ? 'text-delta-up' : 'text-delta-down')}
        aria-hidden
      />
      <span className="sr-only">{words}</span>
    </span>
  );
}

/**
 * Supplier names run past forty characters — "CHANGZHOU HUAYUAN TEXTILE (VIETNAM)" — and the grid
 * is content-sized, so left alone one name sets the width of the column for every row and pushes
 * the quantities off the screen. Clamped and elided, with the full name on hover and in the export.
 */
const NAME_FIELD = 't2_group';

function nameCell(value: unknown): React.ReactNode {
  const text = value === null || value === undefined ? '' : String(value);
  return (
    <span className="block max-w-40 truncate" title={text}>
      {text}
    </span>
  );
}

/** Every t2-business grid's base cell: the supplier name clamped, everything else as declared. */
export const withNameClamp: Formatter = (value, column, row = {}) =>
  column.field === NAME_FIELD ? nameCell(value) : defaultFormat(value, column, row);

/** Arrow hard left, figure flush right, so digits still line up under the heading. */
function TrendCell({ arrow, figure }: { arrow: React.ReactNode; figure: React.ReactNode }) {
  return (
    <span className="flex w-full items-center justify-between gap-2">
      {arrow}
      {figure}
    </span>
  );
}

/**
 * Which measure the grids and charts are reading. Both are computed by the same query and arrive in
 * the same response; this selects between columns the server already sent (§4.3).
 *
 * **Value carries a known source defect.** A few hundred lines price a per-cone rate against a
 * per-metre quantity and dominate committed value — see the server's
 * `docs/design/t2-vendor-analysis.md`. Quantity is unaffected. The page no longer says so beside
 * the control: the owner asked for the banner off on 2026-08-18 and is correcting it at source.
 */
export type Measure = 'qty' | 'value';

/**
 * Which pair of source columns the measures read: what was ordered, or what has been invoiced.
 * Bound as a query parameter, so both grids and both charts change together — see report.yaml.
 */
export type Basis = 'ordered' | 'invoiced';

export const BASIS_PHRASE: Readonly<Record<Basis, string>> = {
  ordered: 'ordered',
  invoiced: 'invoiced',
};

export function isBasis(value: string | null | undefined): value is Basis {
  return value === 'ordered' || value === 'invoiced';
}

export const MEASURE_LABEL: Readonly<Record<Measure, string>> = {
  qty: 'Quantity',
  value: 'Value (HKD)',
};

/**
 * The same measure inside a sentence — "committed value (HKD)". Not the label lower-cased: HKD is
 * a currency code and stays upper-case wherever it is written.
 */
export const MEASURE_PHRASE: Readonly<Record<Measure, string>> = {
  qty: 'quantity',
  value: 'value (HKD)',
};

/** The row field each measure totals by, for the charts. */
export const TOTAL_FIELD: Readonly<Record<Measure, string>> = {
  qty: 'qty_total',
  value: 'val_total',
};

const MONTH_FIELDS: Readonly<Record<Measure, readonly string[]>> = {
  qty: ['m01', 'm02', 'm03', 'm04', 'm05', 'm06', 'm07', 'm08', 'm09', 'm10', 'm11', 'm12'],
  value: ['v01', 'v02', 'v03', 'v04', 'v05', 'v06', 'v07', 'v08', 'v09', 'v10', 'v11', 'v12'],
};

const SEASON_FIELDS: Readonly<Record<Measure, readonly string[]>> = {
  qty: ['qty_s1', 'qty_s2', 'qty_s3', 'qty_s4', 'qty_s5', 'qty_s6', 'qty_s7', 'qty_s8'],
  value: ['val_s1', 'val_s2', 'val_s3', 'val_s4', 'val_s5', 'val_s6', 'val_s7', 'val_s8'],
};

/** Everything belonging to one measure, so the other one can be left out of a grid entirely. */
const MEASURE_FIELDS: Readonly<Record<Measure, ReadonlySet<string>>> = {
  qty: new Set([
    ...MONTH_FIELDS.qty,
    ...SEASON_FIELDS.qty,
    'qty_other',
    'qty_total',
    'qty_share',
    'qty_year_2',
    'qty_year_1',
    'qty_year_0',
  ]),
  value: new Set([
    ...MONTH_FIELDS.value,
    ...SEASON_FIELDS.value,
    'val_other',
    'val_total',
    'val_share',
    'val_year_2',
    'val_year_1',
    'val_year_0',
  ]),
};

/** The columns of the measure not on screen, dropped. by-year keeps both and does not call this. */
export function onlyMeasure(
  columns: readonly ReportColumn[],
  measure: Measure,
): ReportColumn[] {
  const other = measure === 'qty' ? MEASURE_FIELDS.value : MEASURE_FIELDS.qty;
  return columns.filter((column) => !other.has(column.field));
}

// ---------------------------------------------------------------------------------------------
// by-year
// ---------------------------------------------------------------------------------------------

/**
 * The quantity columns are titled with the years the response returned, not with "Year -2" — a
 * reader comparing 2024 against 2026 should read the years, and the server sends them precisely so
 * the page never derives them from the filter (report.yaml). The three label columns become the
 * headings and stop being drawn as columns of their own; an empty result keeps the declared
 * fallback labels, there being no row yet to read a year off.
 */
const YEAR_LABEL_OF: Readonly<Record<string, string>> = {
  qty_year_2: 'year_2',
  qty_year_1: 'year_1',
  qty_year_0: 'year_0',
  val_year_2: 'year_2',
  val_year_1: 'year_1',
  val_year_0: 'year_0',
};

const YEAR_LABEL_FIELDS = new Set(Object.values(YEAR_LABEL_OF));

export function yearTitledColumns(
  columns: readonly ReportColumn[],
  rows: readonly Row[],
): ReportColumn[] {
  const first = rows[0];
  return columns
    .filter((column) => !YEAR_LABEL_FIELDS.has(column.field))
    .map((column) => {
      const labelField = YEAR_LABEL_OF[column.field];
      const year =
        labelField === undefined || first === undefined ? null : yearLabel(first[labelField]);
      return year === null ? column : { ...column, label: year };
    });
}

/**
 * Each year against the year before it. The oldest year has nothing to compare against and carries
 * no arrow, which is the honest rendering of "no prior year in this window"; the total carries none
 * either, being a sum across all three rather than a figure with a period of its own.
 */
const YEAR_PRIOR_OF: ReadonlyMap<string, string> = new Map([
  ['qty_year_1', 'qty_year_2'],
  ['qty_year_0', 'qty_year_1'],
  ['val_year_1', 'val_year_2'],
  ['val_year_0', 'val_year_1'],
]);

export const withYearTrend: Formatter = (value, column, row = {}) => {
  const priorField = YEAR_PRIOR_OF.get(column.field);
  const current = toNumber(value);
  const prior = priorField === undefined ? null : toNumber(row[priorField]);
  if (priorField === undefined || current === null || prior === null) {
    return withNameClamp(value, column, row);
  }

  const labelOf = (field: string): string | null => {
    const labelField = YEAR_LABEL_OF[field];
    return labelField === undefined ? null : yearLabel(row[labelField]);
  };

  return (
    <TrendCell
      arrow={
        <TrendArrow
          current={current}
          prior={prior}
          currentLabel={labelOf(column.field)}
          priorLabel={labelOf(priorField)}
        />
      }
      figure={defaultFormat(value, column, row)}
    />
  );
};

/** The years the response returned, keyed by the quantity column each one titles. */
export function yearLabelsOf(rows: readonly Row[]): ReadonlyMap<string, string> {
  const first = rows[0];
  const out = new Map<string, string>();
  if (first === undefined) {
    return out;
  }
  for (const [field, labelField] of Object.entries(YEAR_LABEL_OF)) {
    const year = yearLabel(first[labelField]);
    if (year !== null) {
      out.set(field, year);
    }
  }
  return out;
}

export { YEAR_PRIOR_OF };

// ---------------------------------------------------------------------------------------------
// by-month
// ---------------------------------------------------------------------------------------------

const MONTH_FIELD_SET: ReadonlySet<string> = new Set([
  ...MONTH_FIELDS.qty,
  ...MONTH_FIELDS.value,
]);

/**
 * The months a chosen year has actually had, as the fields of the measure on screen. A month still
 * ahead of today returns zero from `SUM(CASE WHEN order_month = …)` and would be drawn as a real
 * nothing beside eight real figures — a run of zeros that reads as "we stopped buying" rather than
 * "it has not happened yet".
 *
 * A past year keeps all twelve. `today` is a parameter so this is testable, and so a tab left open
 * across midnight on 31 December cannot keep yesterday's idea of "current" (§4.4).
 */
export function monthsSoFar(
  year: string | null,
  measure: Measure,
  today: Date = new Date(),
): readonly string[] {
  const fields = MONTH_FIELDS[measure];
  const chosen = Number(year);
  if (!Number.isFinite(chosen) || chosen < today.getFullYear()) {
    return fields;
  }
  if (chosen > today.getFullYear()) {
    return [];
  }
  return fields.slice(0, today.getMonth() + 1);
}

/** Drops the months that have not happened yet, keeping every other declared column. */
export function monthsSoFarColumns(
  columns: readonly ReportColumn[],
  visible: readonly string[],
): ReportColumn[] {
  const keep = new Set(visible);
  return columns.filter((column) => !MONTH_FIELD_SET.has(column.field) || keep.has(column.field));
}

export function monthLabels(columns: readonly ReportColumn[]): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  for (const column of columns) {
    if (MONTH_FIELD_SET.has(column.field)) {
      out.set(column.field, column.label);
    }
  }
  return out;
}

/**
 * Each period against the one before it, across the columns on screen. The leftmost carries no
 * arrow: the response holds nothing before it to read, and comparing against nothing would invent
 * a claim.
 */
export function periodTrendFormat(
  visible: readonly string[],
  labelOf: ReadonlyMap<string, string>,
): Formatter {
  const preceding = precedingOf(visible);

  return (value, column, row = {}) => {
    const priorField = preceding.get(column.field);
    const current = toNumber(value);
    const prior = priorField === undefined ? null : toNumber(row[priorField]);
    if (priorField === undefined || current === null || prior === null) {
      return withNameClamp(value, column, row);
    }
    return (
      <TrendCell
        arrow={
          <TrendArrow
            current={current}
            prior={prior}
            currentLabel={labelOf.get(column.field) ?? null}
            priorLabel={labelOf.get(priorField) ?? null}
          />
        }
        figure={defaultFormat(value, column, row)}
      />
    );
  };
}

/**
 * The shape of the year in one cell — one bar per month. Drawn directly rather than through
 * Recharts because it is one `<svg>` per row across a couple of hundred rows, where a
 * `ResponsiveContainer` apiece would put that many resize observers on the page. §4.5 picks
 * Recharts for a chart a reader reads and interacts with; this is a glyph, and the icon rule it
 * sits near governs icons, which this is not.
 *
 * Bars stand on a zero baseline and are scaled to the row's own peak: heights are comparable
 * across the year within a row and deliberately not across rows, which is what "shape of the year"
 * means. The figures themselves are in the month cells beside it, so this is `aria-hidden` —
 * repeating a row's data in a second voice is noise, not access (§4.9).
 */
function MonthBars({ points }: { points: readonly number[] }) {
  if (points.length === 0) {
    return null;
  }
  const high = Math.max(...points, 0);
  const slot = 100 / points.length;
  // A gap of a quarter slot, so twelve bars still read as twelve and not as a filled block.
  const width = Math.max(slot * 0.72, 0.5);
  const base = 19;
  const last = points.length - 1;

  return (
    <svg
      viewBox="0 0 100 20"
      preserveAspectRatio="none"
      className="h-5 w-full text-chart-1"
      aria-hidden
      focusable="false"
    >
      {points.map((point, index) => {
        const height = high === 0 ? 0 : (Math.max(point, 0) / high) * 18;
        return (
          <rect
            key={index}
            x={index * slot + (slot - width) / 2}
            // A month with nothing still gets a hairline, so an empty month reads as zero rather
            // than as a month the grid forgot to draw.
            y={base - Math.max(height, 0.6)}
            width={width}
            height={Math.max(height, 0.6)}
            fill="currentColor"
            // The latest month leads; the ones behind it sit back so the eye lands on where the
            // year has got to.
            fillOpacity={index === last ? 1 : 0.55}
          />
        );
      })}
      {/* The zero line the bars stand on. */}
      <rect x={0} y={base} width={100} height={0.5} fill="currentColor" fillOpacity={0.25} />
    </svg>
  );
}

/**
 * A column the server did not declare, and deliberately not exported: it holds no figure of its
 * own, and `exportColumns` skips any leaf without an `exportLabel` (§5.9). Unsortable for the same
 * reason — there is no value here to order by.
 */
/** Period → the period before it, over the columns on screen. The first one has none. */
export function precedingOf(visible: readonly string[]): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  visible.forEach((field, index) => {
    const prior = visible[index - 1];
    if (prior !== undefined) {
      out.set(field, prior);
    }
  });
  return out;
}

/**
 * The totals row's own arrows — the same comparison the rows carry, made across every supplier at
 * once: all suppliers' 2026 against all suppliers' 2025.
 *
 * The two figures are summed here rather than read from a column, because the comparison needs the
 * prior period's total while the row is rendering the current one. A straight sum is the only
 * aggregate the client may derive (§4.3), and the page passes a totals row **only** over a complete
 * result, so these sum the whole answer and not part of it.
 */
export function totalsTrendFormat(
  rows: readonly Row[],
  priorOf: ReadonlyMap<string, string>,
  labelOf: ReadonlyMap<string, string>,
): (value: number, columnId: string) => React.ReactNode {
  const fields = new Set([...priorOf.keys(), ...priorOf.values()]);
  const sums = new Map<string, number>();
  for (const row of rows) {
    for (const field of fields) {
      sums.set(field, (sums.get(field) ?? 0) + (toNumber(row[field]) ?? 0));
    }
  }

  return (value, columnId) => {
    const priorField = priorOf.get(columnId);
    const prior = priorField === undefined ? undefined : sums.get(priorField);
    if (priorField === undefined || prior === undefined) {
      return integer.format(value);
    }
    return (
      <TrendCell
        arrow={
          <TrendArrow
            current={value}
            prior={prior}
            currentLabel={labelOf.get(columnId) ?? null}
            priorLabel={labelOf.get(priorField) ?? null}
          />
        }
        figure={integer.format(value)}
      />
    );
  };
}

export function sparklineColumn(visible: readonly string[]): ColumnDef<Row> {
  return {
    id: 'month_trend',
    header: 'Trend',
    enableSorting: false,
    size: 84,
    minSize: 64,
    meta: { align: 'left', headerAlign: 'left' },
    cell: ({ row }) => (
      <MonthBars points={visible.map((field) => toNumber(row.original[field]) ?? 0)} />
    ),
  };
}

// ---------------------------------------------------------------------------------------------
// by-season
// ---------------------------------------------------------------------------------------------

/**
 * Eight fixed quantity slots and the column carrying the season code that titles each — the same
 * arrangement by-year uses for its three years, and for the same reason: the window decides which
 * seasons those are, so the labels travel with the data (report.yaml, by-season.sql).
 *
 * A slot the window did not fill has no code and is dropped rather than drawn as an untitled column
 * of zeros.
 */
const SEASON_LABEL_OF: Readonly<Record<string, string>> = {
  qty_s1: 'season_1',
  qty_s2: 'season_2',
  qty_s3: 'season_3',
  qty_s4: 'season_4',
  qty_s5: 'season_5',
  qty_s6: 'season_6',
  qty_s7: 'season_7',
  qty_s8: 'season_8',
  val_s1: 'season_1',
  val_s2: 'season_2',
  val_s3: 'season_3',
  val_s4: 'season_4',
  val_s5: 'season_5',
  val_s6: 'season_6',
  val_s7: 'season_7',
  val_s8: 'season_8',
};

const SEASON_LABEL_FIELDS = new Set(Object.values(SEASON_LABEL_OF));

/** The season codes the response returned, keyed by the quantity column each one titles. */
export function seasonLabelsOf(rows: readonly Row[]): ReadonlyMap<string, string> {
  const first = rows[0];
  const out = new Map<string, string>();
  if (first === undefined) {
    return out;
  }
  for (const [field, labelField] of Object.entries(SEASON_LABEL_OF)) {
    const code = first[labelField];
    if (typeof code === 'string' && code.trim() !== '') {
      out.set(field, code);
    }
  }
  return out;
}

/** The filled slots of the measure on screen, oldest season first. */
export function seasonFields(rows: readonly Row[], measure: Measure): readonly string[] {
  const labels = seasonLabelsOf(rows);
  return SEASON_FIELDS[measure].filter((field) => labels.has(field));
}

/** Titles each filled slot with its season code, drops the empty slots and the code columns. */
export function seasonTitledColumns(
  columns: readonly ReportColumn[],
  rows: readonly Row[],
): ReportColumn[] {
  const labels = seasonLabelsOf(rows);
  const empty = rows.length === 0;
  return columns
    .filter((column) => !SEASON_LABEL_FIELDS.has(column.field))
    .filter((column) => empty || !(column.field in SEASON_LABEL_OF) || labels.has(column.field))
    .map((column) => {
      const label = labels.get(column.field);
      return label === undefined ? column : { ...column, label };
    });
}
